import { useEffect, useRef, useState } from 'react'
import { Camera, Image as ImageIcon, X } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  useDeleteStepPhoto,
  useUpdateStepPhoto,
  useUploadStepPhoto,
} from '@/features/projects/use-photos'
import { StepPhotoFullscreenModal } from './step-photo-fullscreen-modal'
import type { StepPhoto } from '@/api/types'

interface StepPhotoGridProps {
  projectId: string
  stepId: string
  photos: StepPhoto[]
  readOnly: boolean
}

const DELETE_CONFIRM_THRESHOLD = 5
const ACCEPT = 'image/png,image/jpeg,image/webp'

/**
 * Phase 8 — inline thumbnail grid below an Action's content.
 *
 * <p>In edit mode: drag-and-drop zone overlays the grid; "Add photo" buttons (file picker + camera
 * capture); each thumbnail has an X button (with confirmation past 5 photos) and inline-editable
 * caption. In read-only mode (viewer / doing): thumbnails only, click-to-fullscreen.
 */
export const StepPhotoGrid = ({ projectId, stepId, photos, readOnly }: StepPhotoGridProps) => {
  const upload = useUploadStepPhoto(projectId, stepId)

  const [fullscreenIdx, setFullscreenIdx] = useState<number | null>(null)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const sorted = [...photos].sort((a, b) => a.sortOrder - b.sortOrder)

  const handleFiles = async (files: FileList | null) => {
    if (!files) return
    for (const f of Array.from(files)) {
      try {
        await upload.mutateAsync({ blob: f })
      } catch {
        toast.error('Failed to upload photo')
      }
    }
  }

  if (readOnly && photos.length === 0) return null

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Photos
        </span>
        {!readOnly && (
          <div className="flex items-center gap-1">
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              multiple
              className="hidden"
              onChange={(e) => {
                void handleFiles(e.target.files)
                e.currentTarget.value = ''
              }}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                void handleFiles(e.target.files)
                e.currentTarget.value = ''
              }}
            />
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() => fileInputRef.current?.click()}
              disabled={upload.isPending}
            >
              <ImageIcon className="h-3.5 w-3.5" />
              Add photo
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1 sm:hidden"
              onClick={() => cameraInputRef.current?.click()}
              disabled={upload.isPending}
              aria-label="Take photo with camera"
            >
              <Camera className="h-3.5 w-3.5" />
              Camera
            </Button>
          </div>
        )}
      </div>

      {sorted.length === 0 ? (
        !readOnly && (
          <div
            data-testid="step-photo-drop-zone"
            onDragOver={(e) => {
              e.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault()
              setDragging(false)
              void handleFiles(e.dataTransfer.files)
            }}
            className={`rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground transition-colors ${
              dragging ? 'border-primary bg-primary/5' : ''
            }`}
          >
            Drag photos here or click &ldquo;Add photo&rdquo;
          </div>
        )
      ) : (
        <div
          data-testid="step-photo-grid"
          onDragOver={
            !readOnly
              ? (e) => {
                  e.preventDefault()
                  setDragging(true)
                }
              : undefined
          }
          onDragLeave={!readOnly ? () => setDragging(false) : undefined}
          onDrop={
            !readOnly
              ? (e) => {
                  e.preventDefault()
                  setDragging(false)
                  void handleFiles(e.dataTransfer.files)
                }
              : undefined
          }
          className={`grid grid-cols-2 gap-2 sm:grid-cols-4 ${
            dragging ? 'rounded-md ring-2 ring-primary/40' : ''
          }`}
        >
          {sorted.map((photo, idx) => (
            <StepPhotoThumb
              key={photo.id}
              projectId={projectId}
              stepId={stepId}
              photo={photo}
              readOnly={readOnly}
              photoCount={sorted.length}
              onOpen={() => setFullscreenIdx(idx)}
              onRequestDelete={() => setPendingDeleteId(photo.id)}
            />
          ))}
        </div>
      )}

      <StepPhotoFullscreenModal
        open={fullscreenIdx !== null}
        imageUrl={fullscreenIdx !== null ? (sorted[fullscreenIdx]?.displayUrl ?? null) : null}
        caption={fullscreenIdx !== null ? (sorted[fullscreenIdx]?.caption ?? null) : null}
        onClose={() => setFullscreenIdx(null)}
      />

      {pendingDeleteId && (
        <DeleteConfirmDialog
          projectId={projectId}
          stepId={stepId}
          photoId={pendingDeleteId}
          requireConfirm={sorted.length >= DELETE_CONFIRM_THRESHOLD}
          onClose={() => setPendingDeleteId(null)}
        />
      )}
    </div>
  )
}

interface StepPhotoThumbProps {
  projectId: string
  stepId: string
  photo: StepPhoto
  readOnly: boolean
  photoCount: number
  onOpen: () => void
  onRequestDelete: () => void
}

const StepPhotoThumb = ({
  projectId,
  stepId,
  photo,
  readOnly,
  photoCount,
  onOpen,
  onRequestDelete,
}: StepPhotoThumbProps) => {
  const update = useUpdateStepPhoto(projectId, stepId, photo.id)
  const deleteMutation = useDeleteStepPhoto(projectId, stepId)
  const [captionDraft, setCaptionDraft] = useState(photo.caption ?? '')
  const [editingCaption, setEditingCaption] = useState(false)

  // Keep draft in sync if the photo updates from elsewhere.
  useEffect(() => {
    if (!editingCaption) setCaptionDraft(photo.caption ?? '')
  }, [photo.caption, editingCaption])

  const saveCaption = () => {
    setEditingCaption(false)
    const next = captionDraft.trim()
    const current = photo.caption ?? ''
    if (next === current) return
    update.mutate(
      { caption: next.length === 0 ? null : next },
      { onError: () => toast.error('Failed to save caption') }
    )
  }

  const handleDelete = () => {
    if (photoCount >= DELETE_CONFIRM_THRESHOLD) {
      onRequestDelete()
      return
    }
    deleteMutation.mutate(photo.id, {
      onError: () => toast.error('Failed to delete photo'),
    })
  }

  return (
    <div className="space-y-1">
      <button
        type="button"
        className="group relative aspect-square overflow-hidden rounded-md border bg-muted"
        onClick={onOpen}
      >
        <img
          src={photo.thumbUrl}
          alt={photo.caption ?? 'Step photo'}
          className="h-full w-full object-cover"
          loading="lazy"
        />
        {!readOnly && (
          <span
            role="button"
            tabIndex={0}
            aria-label="Delete photo"
            onClick={(e) => {
              e.stopPropagation()
              handleDelete()
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                handleDelete()
              }
            }}
            className="absolute right-1 top-1 hidden h-6 w-6 items-center justify-center rounded-full bg-background/90 text-foreground shadow group-hover:flex hover:bg-destructive hover:text-destructive-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </span>
        )}
      </button>
      {readOnly ? (
        photo.caption && <p className="text-xs text-muted-foreground">{photo.caption}</p>
      ) : (
        <input
          type="text"
          value={captionDraft}
          maxLength={500}
          placeholder="Add caption…"
          onFocus={() => setEditingCaption(true)}
          onChange={(e) => setCaptionDraft(e.target.value)}
          onBlur={saveCaption}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              ;(e.currentTarget as HTMLInputElement).blur()
            }
            if (e.key === 'Escape') {
              setCaptionDraft(photo.caption ?? '')
              setEditingCaption(false)
              ;(e.currentTarget as HTMLInputElement).blur()
            }
          }}
          className="w-full rounded-sm bg-transparent text-xs text-muted-foreground outline-none placeholder:text-muted-foreground/60 focus:ring-1 focus:ring-primary"
        />
      )}
    </div>
  )
}

interface DeleteConfirmDialogProps {
  projectId: string
  stepId: string
  photoId: string
  requireConfirm: boolean
  onClose: () => void
}

const DeleteConfirmDialog = ({
  projectId,
  stepId,
  photoId,
  requireConfirm,
  onClose,
}: DeleteConfirmDialogProps) => {
  const deleteMutation = useDeleteStepPhoto(projectId, stepId)
  if (!requireConfirm) {
    deleteMutation.mutate(photoId)
    onClose()
    return null
  }
  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this photo?</AlertDialogTitle>
          <AlertDialogDescription>
            The photo will be removed from this step and the underlying file deleted. This
            can&apos;t be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              deleteMutation.mutate(photoId, {
                onError: () => toast.error('Failed to delete photo'),
                onSettled: onClose,
              })
            }}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
